import { Component, model, input, effect, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { DialogModule } from 'primeng/dialog';
import { BlockScheduleFileModel } from '../../../../models/project.model';
import { OciService } from '../../../../services/oci.service';
import { FileControlValue, LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';
import { ProjecttemplateService } from '../../../../services/projecttemplate.service';
import { downloadChecklistFileResponse } from '../../../../utils/checklist-file-download.utils';

@Component({
  selector: 'app-signature-control',
  standalone: true,
  imports: [CommonModule, ButtonModule, ImageModule, DialogModule],
  template: `
    <div class="checklist-control-container">
      <label class="checklist-control-label">
        {{ lineItem().name }}
        @if (lineItem().isRequired) {
          <span class="required-indicator">*</span>
        }
        <button
          type="button"
          pButton
          icon="pi pi-volume-up"
          [rounded]="true"
          [text]="true"
          size="small"
          (click)="speakLabel()"
          class="speaker-btn ml-2"
          [attr.aria-label]="'Read label: ' + lineItem().name"
        ></button>
      </label>

      <div class="checklist-control-input">
        @if (!hasExistingSignature && !hasNewSignature) {
          <!-- No signature yet - show canvas -->
          <div class="signature-pad-container">
            <canvas
              #signatureCanvas
              class="signature-canvas"
              [class.disabled]="disabled()"
            ></canvas>
            <div class="flex gap-2 mt-2">
              <p-button
                label="Clear"
                icon="pi pi-times"
                [outlined]="true"
                size="small"
                (click)="clearSignature()"
                [disabled]="disabled() || !hasDrawn"
              />
              <p-button
                label="Save Signature"
                icon="pi pi-check"
                size="small"
                (click)="saveSignature()"
                [disabled]="disabled() || !hasDrawn"
              />
            </div>
            @if (!hasDrawn) {
              <div class="text-sm text-500 mt-2">Draw your signature above</div>
            }
          </div>
        } @else {
          <!-- Signature exists - show preview -->
          <div class="signature-preview-container">
            @if (signatureImageUrl) {
              <div class="signature-preview" (click)="previewSignature()">
                <img
                  [src]="signatureImageUrl"
                  alt="Signature"
                  style="max-width: 100%; max-height: 200px; object-fit: contain; border: 1px solid var(--surface-border); border-radius: 6px; cursor: pointer; background: white;"
                />
              </div>
            } @else {
              <div class="signature-placeholder">
                <i class="pi pi-spin pi-spinner text-4xl text-400"></i>
              </div>
            }
            @if (!disabled()) {
              <div class="flex gap-2 mt-2">
                <p-button
                  label="Re-sign"
                  icon="pi pi-pencil"
                  [outlined]="true"
                  size="small"
                  (click)="resignSignature()"
                />
                <p-button
                  label="Download"
                  icon="pi pi-download"
                  [outlined]="true"
                  size="small"
                  (click)="downloadSignature()"
                />
              </div>
            }
          </div>
        }
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        }
      </div>
    </div>

    <!-- Preview Dialog -->
    <p-dialog [(visible)]="showPreviewDialog" [modal]="true" [style]="{ width: '90vw' }" header="Signature Preview" [dismissableMask]="true">
      <div class="flex align-items-center justify-content-center" style="min-height: 400px;">
        @if (signatureImageUrl) {
          <p-image [src]="signatureImageUrl" alt="Signature" [preview]="true">
            <ng-template pTemplate="image">
              <img [src]="signatureImageUrl" alt="Signature" style="max-width: 100%; max-height: 70vh; object-fit: contain; background: white;" />
            </ng-template>
          </p-image>
        }
      </div>
    </p-dialog>
  `,
  styles: [`
    .checklist-control-container {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.75rem;
      border: 1px solid var(--surface-border);
      border-radius: 6px;
      background: var(--surface-0);
    }
    .checklist-control-label {
      font-weight: 500;
    }
    .required-indicator {
      color: var(--color-error-mid);
      font-weight: bold;
      margin-left: 2px;
    }
    .signature-pad-container {
      display: flex;
      flex-direction: column;
    }
    .signature-canvas {
      width: 100%;
      height: 200px;
      border: 2px dashed var(--surface-border);
      border-radius: 6px;
      cursor: crosshair;
      background: white;
      touch-action: none;
    }
    .signature-canvas.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    .signature-preview-container {
      display: flex;
      flex-direction: column;
    }
    .signature-preview {
      cursor: pointer;
    }
    .signature-placeholder {
      width: 100%;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--surface-border);
      border-radius: 6px;
      background: var(--surface-100);
    }
  `]
})
export class SignatureControlComponent implements AfterViewInit {
  @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  lineItem = input.required<LineItemModel>();
  value = model<FileControlValue>({ newFiles: [], existingFileIds: [] });
  disabled = input<boolean>(false);
  showError = input<boolean>(false);
  existingFilesData = input<BlockScheduleFileModel[]>([]);

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;

  hasDrawn = false;
  hasNewSignature = false;
  hasExistingSignature = false;
  signatureImageUrl: string | null = null;
  showPreviewDialog = false;

  private existingSignature: BlockScheduleFileModel | null = null;
  private validationService = inject(ValidationService);

  errors = computed(() => {
    if (!this.showError()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  constructor(
    private ociService: OciService,
    private cdr: ChangeDetectorRef,
    private projecttemplateService: ProjecttemplateService
  ) {
    // Sync existing files from input and load signature
    effect(() => {
      const files = this.existingFilesData() || [];
      if (files.length > 0) {
        this.existingSignature = files[0]; // Signature control should only have one file
        this.hasExistingSignature = true;
        this.loadSignatureImage(files[0]);
      } else {
        this.existingSignature = null;
        this.hasExistingSignature = false;
        this.signatureImageUrl = null;
      }
      this.cdr.markForCheck();
    });

    // Check if there's a new signature file
    effect(() => {
      const val = this.value();
      if (val && val.newFiles && val.newFiles.length > 0) {
        this.hasNewSignature = true;
        // Generate preview from new file
        const file = val.newFiles[0];
        if (file instanceof File) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.signatureImageUrl = e.target?.result as string;
            this.cdr.markForCheck();
          };
          reader.readAsDataURL(file);
        }
      } else {
        this.hasNewSignature = false;
        // If no existing signature either, clear the preview
        if (!this.hasExistingSignature) {
          this.signatureImageUrl = null;
        }
      }
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    // Use setTimeout to ensure the canvas is fully laid out before initialization
    setTimeout(() => {
      this.initializeCanvas();
    }, 0);
  }

  private initializeCanvas(): void {
    // Only initialize if canvas is rendered (no existing/new signature)
    if (!this.canvasRef) {
      return;
    }

    this.canvas = this.canvasRef.nativeElement;
    this.ctx = this.canvas.getContext('2d')!;

    // Set canvas internal dimensions using offsetWidth/Height and account for border
    // offsetWidth/Height includes borders, so subtract them to get drawable area
    const borderWidth = 4; // 2px border on each side = 4px total
    this.canvas.width = this.canvas.offsetWidth - borderWidth;
    this.canvas.height = this.canvas.offsetHeight - borderWidth;

    // Set drawing styles - ensure black color
    this.ctx.strokeStyle = '#000000';
    this.ctx.lineWidth = 3;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    // Add event listeners
    this.canvas.addEventListener('mousedown', this.startDrawing.bind(this));
    this.canvas.addEventListener('mousemove', this.draw.bind(this));
    this.canvas.addEventListener('mouseup', this.stopDrawing.bind(this));
    this.canvas.addEventListener('mouseleave', this.stopDrawing.bind(this));

    // Touch support
    this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.stopDrawing.bind(this));
  }

  private getTouchPos(e: TouchEvent): { x: number; y: number } {
    if (!this.canvas) return { x: 0, y: 0 };

    const rect = this.canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const borderWidth = 2; // Account for border offset
    return {
      x: touch.clientX - rect.left - borderWidth,
      y: touch.clientY - rect.top - borderWidth
    };
  }

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const pos = this.getTouchPos(e);
    this.lastX = pos.x;
    this.lastY = pos.y;
    this.isDrawing = true;
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();
    if (!this.isDrawing || this.disabled()) return;
    const pos = this.getTouchPos(e);
    this.drawLine(this.lastX, this.lastY, pos.x, pos.y);
    this.lastX = pos.x;
    this.lastY = pos.y;
    if (!this.hasDrawn) {
      this.hasDrawn = true;
      this.cdr.markForCheck();
    }
  }

  private startDrawing(e: MouseEvent): void {
    if (this.disabled() || !this.canvas) return;
    this.isDrawing = true;
    const rect = this.canvas.getBoundingClientRect();
    const borderWidth = 2; // Account for border offset
    this.lastX = e.clientX - rect.left - borderWidth;
    this.lastY = e.clientY - rect.top - borderWidth;
  }

  private draw(e: MouseEvent): void {
    if (!this.isDrawing || this.disabled() || !this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    const borderWidth = 2; // Account for border offset
    const x = e.clientX - rect.left - borderWidth;
    const y = e.clientY - rect.top - borderWidth;
    this.drawLine(this.lastX, this.lastY, x, y);
    this.lastX = x;
    this.lastY = y;
    if (!this.hasDrawn) {
      this.hasDrawn = true;
      this.cdr.markForCheck();
    }
  }

  private drawLine(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.ctx) return;

    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }

  private stopDrawing(): void {
    this.isDrawing = false;
  }

  clearSignature(): void {
    if (!this.ctx || !this.canvas) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.hasDrawn = false;
    this.cdr.markForCheck();
  }

  saveSignature(): void {
    if (!this.hasDrawn || !this.canvas) return;

    // Convert canvas to blob
    this.canvas.toBlob((blob) => {
      if (blob) {
        // Create a File object from the blob
        const file = new File([blob], `signature-${Date.now()}.png`, { type: 'image/png' });

        // Update value model
        this.value.set({
          newFiles: [file],
          existingFileIds: []
        });

        this.cdr.markForCheck();
      }
    }, 'image/png');
  }

  resignSignature(): void {
    // Clear existing signature
    if (this.existingSignature) {
      this.value.set({
        newFiles: [],
        existingFileIds: []
      });
    }

    // Reset state
    this.hasNewSignature = false;
    this.hasExistingSignature = false;
    this.signatureImageUrl = null;
    this.hasDrawn = false;

    // Reinitialize canvas after view update
    setTimeout(() => {
      this.initializeCanvas();
      this.cdr.markForCheck();
    }, 100);
  }

  private loadSignatureImage(signature: BlockScheduleFileModel): void {
    if (!signature.thumbnailPath) return;

    this.ociService.getOciResourceEnhancedWithoutSpinner(signature.thumbnailPath).subscribe({
      next: (blob: Blob) => {
        this.signatureImageUrl = URL.createObjectURL(blob);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching signature:', err);
      }
    });
  }

  previewSignature(): void {
    this.showPreviewDialog = true;
  }

  downloadSignature(): void {
    if (!this.existingSignature?.id) return;

    this.projecttemplateService.downloadChecklistAttachment(this.existingSignature.id).subscribe({
      next: response => downloadChecklistFileResponse(response, this.existingSignature?.fileName || 'signature.png'),
      error: err => console.error('Error downloading signature:', err)
    });
  }

  private speechService = inject(SpeechService);

  speakLabel(): void {
    const labelText = this.lineItem().name || 'Signature';
    this.speechService.speak(labelText);
  }
}
